import { useRef, useState } from "react";
import { api, ApiError, type DiaEspelho } from "../api";

/**
 * "ESQUECI DE BATER" E O ATESTADO — o pedido nasce no DIA, dentro do espelho.
 *
 * A pessoa toca no dia errado e resolve ali: não existe aba nova, não existe
 * formulário em branco pedindo a data de novo. O dia já vem escolhido, e os
 * quatro campos já vêm preenchidos com o que está na folha — assim ela só
 * mexe no horário que faltou, em vez de redigitar o dia inteiro e errar outro.
 *
 * NADA DISSO MUDA A FOLHA SOZINHO. É pedido: o DP confere e aprova. O horário
 * que a pessoa digita aqui é o que ela AFIRMA ter cumprido — a batida original,
 * com foto e localização, continua guardada e intocada.
 *
 * O atestado tem o arquivo OBRIGATÓRIO e uma distinção que evita erro caro:
 * "fui ao médico e voltei" (parcial) não transforma o dia inteiro em atestado —
 * senão uma declaração de duas horas apagaria a jornada toda.
 */

interface Props {
  dia: DiaEspelho;
  onEnviado: (mensagem: string) => void;
  onFechar: () => void;
}

const MAX_MB = 10;

const DIAS_SEMANA = [
  "domingo", "segunda-feira", "terça-feira", "quarta-feira",
  "quinta-feira", "sexta-feira", "sábado",
];
const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/** "2026-09-03" → "quarta-feira, 3 de setembro". */
function porExtenso(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${DIAS_SEMANA[d.getUTCDay()]}, ${d.getUTCDate()} de ${MESES[d.getUTCMonth()]}`;
}

function tamanho(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

export default function PedirCorrecao({ dia, onEnviado, onFechar }: Props) {
  const [aba, setAba] = useState<"correcao" | "atestado">("correcao");

  return (
    <div className="correcao">
      <div className="correcao-topo">
        <button className="botao-texto" onClick={onFechar}>
          ← Voltar ao mês
        </button>
        <strong className="correcao-dia">{porExtenso(dia.data)}</strong>
      </div>

      <div className="correcao-atual">
        <span className="correcao-rotulo">Na sua folha hoje</span>
        <div className="dia-marcacoes">
          <span>{dia.entrada ?? "--:--"}</span>
          <span>{dia.saida_almoco ?? "--:--"}</span>
          <span>{dia.retorno_almoco ?? "--:--"}</span>
          <span>{dia.saida ?? "--:--"}</span>
        </div>
        {dia.situacao && dia.situacao !== "Normal" && <span className="correcao-situacao">{dia.situacao}</span>}
      </div>

      {/* Pedido já enviado para este dia: mostrar ANTES do formulário evita a
          pessoa mandar o mesmo pedido de novo por não ter visto o primeiro. */}
      {dia.pedido && (
        <div
          className={`correcao-pedido ${
            dia.pedido.status === "Pendente"
              ? "pedido-aguardando"
              : dia.pedido.status === "Aprovada"
                ? "pedido-aprovado"
                : "pedido-recusado"
          }`}
        >
          <strong>
            {dia.pedido.tipo === "atestado" ? "Atestado" : "Correção"}{" "}
            {dia.pedido.status === "Pendente"
              ? "aguardando o DP"
              : dia.pedido.status === "Aprovada"
                ? "aprovada pelo DP"
                : "recusada pelo DP"}
          </strong>
          <span>
            Enviado em {dia.pedido.criado_em}
            {dia.pedido.status === "Recusada" && dia.pedido.motivo ? ` — ${dia.pedido.motivo}` : ""}
          </span>
          {dia.pedido.status === "Pendente" && (
            <span className="correcao-substitui">
              Se mandar outro pedido para este dia, ele substitui o que está na fila.
            </span>
          )}
        </div>
      )}

      <div className="correcao-abas">
        <button
          className={aba === "correcao" ? "correcao-aba correcao-aba-ativa" : "correcao-aba"}
          onClick={() => setAba("correcao")}
        >
          Corrigir horário
        </button>
        <button
          className={aba === "atestado" ? "correcao-aba correcao-aba-ativa" : "correcao-aba"}
          onClick={() => setAba("atestado")}
        >
          Mandar atestado
        </button>
      </div>

      {aba === "correcao" ? (
        <FormCorrecao dia={dia} onEnviado={onEnviado} onFechar={onFechar} />
      ) : (
        <FormAtestado dia={dia} onEnviado={onEnviado} onFechar={onFechar} />
      )}
    </div>
  );
}

/**
 * Componentes de NÍVEL DE MÓDULO de propósito: declarados dentro do pai, seriam
 * recriados a cada tecla e o campo perderia o foco no meio da digitação — ver a
 * memória react-remontagem-componente-interno.
 */
function FormCorrecao({ dia, onEnviado, onFechar }: Props) {
  const [entrada, setEntrada] = useState(dia.entrada ?? "");
  const [saidaAlmoco, setSaidaAlmoco] = useState(dia.saida_almoco ?? "");
  const [retornoAlmoco, setRetornoAlmoco] = useState(dia.retorno_almoco ?? "");
  const [saida, setSaida] = useState(dia.saida ?? "");
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      const r = await api.pedirCorrecao({
        data: dia.data,
        entrada,
        saida_almoco: saidaAlmoco,
        retorno_almoco: retornoAlmoco,
        saida,
        motivo: motivo.trim(),
      });
      onEnviado(r.mensagem);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível enviar o pedido.");
      setEnviando(false);
    }
  }

  return (
    <form className="painel-form" onSubmit={enviar}>
      <p className="explica">
        Escreva o horário que você realmente cumpriu. Ele entra na folha{" "}
        <strong>depois que o DP aprovar</strong> — a batida que você fez continua guardada.
      </p>

      <div className="campos-lado">
        <label className="campo">
          <span>Entrada</span>
          <input type="time" value={entrada} onChange={(e) => setEntrada(e.target.value)} />
        </label>
        <label className="campo">
          <span>Saída para o almoço</span>
          <input type="time" value={saidaAlmoco} onChange={(e) => setSaidaAlmoco(e.target.value)} />
        </label>
        <label className="campo">
          <span>Volta do almoço</span>
          <input type="time" value={retornoAlmoco} onChange={(e) => setRetornoAlmoco(e.target.value)} />
        </label>
        <label className="campo">
          <span>Saída</span>
          <input type="time" value={saida} onChange={(e) => setSaida(e.target.value)} />
        </label>
      </div>
      <p className="dica">Deixe em branco o horário que não existiu nesse dia.</p>

      <label className="campo">
        <span>O que aconteceu?</span>
        <textarea
          className="correcao-motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          maxLength={300}
          rows={3}
          placeholder="Ex.: o celular ficou sem bateria e não consegui bater a saída às 17:10"
        />
      </label>

      {erro && <div className="aviso-erro">{erro}</div>}

      <div className="botoes">
        <button type="button" className="botao-texto" onClick={onFechar} disabled={enviando}>
          Cancelar
        </button>
        <button type="submit" className="botao-principal" disabled={enviando || motivo.trim().length < 5}>
          {enviando ? "Enviando…" : "Enviar ao DP"}
        </button>
      </div>
    </form>
  );
}

function FormAtestado({ dia, onEnviado, onFechar }: Props) {
  const [inicio, setInicio] = useState(dia.data);
  const [fim, setFim] = useState(dia.data);
  const [parcial, setParcial] = useState(false);
  const [observacao, setObservacao] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const camera = useRef<HTMLInputElement>(null);
  const arquivos = useRef<HTMLInputElement>(null);

  function escolher(files: FileList | null) {
    const f = files?.[0] ?? null;
    setErro(null);
    if (f && f.size > MAX_MB * 1024 * 1024) {
      setErro(`Arquivo muito grande (${tamanho(f.size)}). O limite é ${MAX_MB} MB.`);
      return;
    }
    setArquivo(f);
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!arquivo) {
      setErro("Fotografe o atestado ou escolha o arquivo — sem o documento o DP não pode abonar.");
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      const r = await api.pedirAtestado(arquivo, {
        data_inicio: inicio,
        data_fim: parcial ? inicio : fim,
        parcial,
        observacao: observacao.trim(),
      });
      onEnviado(r.mensagem);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível enviar o atestado.");
      setEnviando(false);
    }
  }

  return (
    <form className="painel-form" onSubmit={enviar}>
      <p className="explica">
        Fotografe o atestado inteiro, com o carimbo e a data legíveis. Ele vai para a sua pasta e
        os dias entram na folha <strong>depois que o DP conferir</strong>.
      </p>

      {/* A câmera do próprio sistema, e não a galeria: foto do iPhone chega em
          HEIC e o Chrome do DP não abre — ver rh-fotos-heic-iphone. */}
      <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => escolher(e.target.files)} />
      <input
        ref={arquivos}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        hidden
        onChange={(e) => escolher(e.target.files)}
      />

      <div className="botoes">
        <button type="button" className="botao-secundario" onClick={() => camera.current?.click()}>
          Fotografar atestado
        </button>
        <button type="button" className="botao-secundario" onClick={() => arquivos.current?.click()}>
          Escolher arquivo
        </button>
      </div>

      {arquivo && (
        <div className="arquivo-escolhido">
          <strong>{arquivo.name}</strong>
          <span>{tamanho(arquivo.size)}</span>
        </div>
      )}

      <label className="caixa-marcar">
        <input type="checkbox" checked={parcial} onChange={(e) => setParcial(e.target.checked)} />
        <span>
          Foi só algumas horas — fui ao médico e voltei a trabalhar no mesmo dia.
        </span>
      </label>

      <div className="campos-lado">
        <label className="campo">
          <span>{parcial ? "Dia" : "Primeiro dia"}</span>
          <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} required />
        </label>
        {!parcial && (
          <label className="campo">
            <span>Último dia</span>
            <input type="date" value={fim} min={inicio} onChange={(e) => setFim(e.target.value)} required />
          </label>
        )}
      </div>

      <label className="campo">
        <span>Observação (opcional)</span>
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          maxLength={300}
          placeholder="Ex.: consulta às 9h, voltei ao serviço às 11h"
        />
      </label>

      {erro && <div className="aviso-erro">{erro}</div>}

      <div className="botoes">
        <button type="button" className="botao-texto" onClick={onFechar} disabled={enviando}>
          Cancelar
        </button>
        <button type="submit" className="botao-principal" disabled={!arquivo || enviando}>
          {enviando ? "Enviando…" : "Enviar ao DP"}
        </button>
      </div>
    </form>
  );
}
